import { useEffect, useRef, useState } from 'react';
import { crmApi, type SavedClient } from './api';

/**
 * "Client company" field.
 *
 * Logged out it is exactly the plain text input it has always been. Logged in,
 * typing three or more characters opens a dropdown of saved clients; picking
 * one fills the whole address block, while a name that does not exist yet is
 * simply typed and gets saved with the quote.
 *
 * Matching is case- and spacing-insensitive on the server (see
 * mgmt/clients.ts), so "Alpha Company", "Alpha company" and "alpha  company"
 * all find — and later update — the same record rather than creating three.
 */

interface Props {
  value: string;
  onChange: (company: string) => void;
  /** Called when an existing client is picked; fills the rest of the form. */
  onSelect: (client: SavedClient) => void;
  enabled: boolean;
  inputStyle: React.CSSProperties;
  id?: string;
}

const MIN_CHARS = 3;
const DEBOUNCE_MS = 200;

export function ClientCompanyInput({ value, onChange, onSelect, enabled, inputStyle, id }: Props) {
  const [matches, setMatches] = useState<SavedClient[]>([]);
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(0);
  const boxRef = useRef<HTMLDivElement>(null);
  // Bumped on every keystroke; a response whose id is stale is discarded, so a
  // slow early request can never overwrite the results of a later one.
  const reqId = useRef(0);

  useEffect(() => {
    if (!enabled || value.trim().length < MIN_CHARS) {
      setMatches([]);
      setOpen(false);
      return;
    }
    const mine = ++reqId.current;
    const t = setTimeout(() => {
      crmApi
        .searchClients(value)
        .then((r) => {
          if (mine !== reqId.current) return;
          setMatches(r.clients);
          setHighlight(0);
          setOpen(r.clients.length > 0);
        })
        .catch(() => { if (mine === reqId.current) { setMatches([]); setOpen(false); } });
    }, DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [value, enabled]);

  // Close when the click lands anywhere else.
  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent) => {
      if (boxRef.current && !boxRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);

  const pick = (c: SavedClient) => {
    onSelect(c);
    setOpen(false);
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!open || matches.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlight((h) => (h + 1) % matches.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlight((h) => (h - 1 + matches.length) % matches.length);
    } else if (e.key === 'Enter') {
      // Only swallow Enter while a suggestion is actually highlighted —
      // otherwise the key keeps its normal meaning for the form.
      e.preventDefault();
      pick(matches[highlight]!);
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  };

  return (
    <div ref={boxRef} style={{ position: 'relative' }}>
      <input
        id={id}
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={onKeyDown}
        onFocus={() => { if (matches.length > 0) setOpen(true); }}
        placeholder="e.g. Acme Industries Inc."
        // The browser's own organization autofill would cover the suggestion
        // list, so it is turned off whenever ours is active.
        autoComplete={enabled ? 'off' : 'organization'}
        maxLength={300}
        style={inputStyle}
      />
      {open && (
        <ul
          style={{
            position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 50,
            margin: '4px 0 0', padding: '4px', listStyle: 'none',
            background: 'white', border: '1px solid #E5E7EB', borderRadius: '6px',
            boxShadow: '0 6px 16px rgba(0,0,0,0.12)', maxHeight: '240px', overflowY: 'auto',
          }}
        >
          {matches.map((c, i) => (
            <li
              key={c.id}
              onMouseEnter={() => setHighlight(i)}
              onMouseDown={(e) => { e.preventDefault(); pick(c); }}
              style={{
                padding: '8px 10px', borderRadius: '4px', cursor: 'pointer',
                background: i === highlight ? '#F3F4F6' : 'transparent',
                fontSize: '14px', color: '#1A1A1A',
              }}
            >
              <div style={{ fontWeight: 500 }}>{c.company}</div>
              {(c.city || c.nic) && (
                <div style={{ fontSize: '12px', color: '#6B7280' }}>
                  {[c.city, c.nic && `NIC ${c.nic}`].filter(Boolean).join(' · ')}
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
